// Job API Service -- with sessionStorage cache (15-minute TTL)

const JSEARCH_API_KEY = import.meta.env.VITE_JSEARCH_API_KEY || '';
const JSEARCH_BASE_URL = 'https://jsearch.p.rapidapi.com';
const CACHE_TTL_MS = 15 * 60 * 1000;

const getCacheKey = (query, location) => `jsearch_${query}_${location}`.replace(/\s+/g, '_').toLowerCase();

const getCached = (query, location) => {
  try {
    const key = getCacheKey(query, location);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch { return null; }
};

const setCache = (query, location, data) => {
  try {
    sessionStorage.setItem(getCacheKey(query, location), JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota exceeded -- ignore */ }
};

export const searchJobs = async ({ 
  query = 'software engineer', 
  location = '', 
  page = 1, 
  numPages = 1,
  datePosted = 'month',
  jobType = 'fulltime',
  skipCache = false
} = {}) => {
  try {
    if (!JSEARCH_API_KEY) {
      console.warn('JSearch API key not configured. Using mock data.');
      return getMockJobs();
    }

    if (!skipCache) {
      const cached = getCached(query, location);
      if (cached) return cached;
    }

    const params = new URLSearchParams({
      query: query || 'software engineer',
      page: page.toString(),
      num_pages: numPages.toString(),
      date_posted: datePosted,
      job_type: jobType,
    });

    if (location && location.trim()) {
      params.append('location', location.trim());
    }

    const response = await fetch(`${JSEARCH_BASE_URL}/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'X-RapidAPI-Key': JSEARCH_API_KEY,
        'X-RapidAPI-Host': 'jsearch.p.rapidapi.com'
      }
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Invalid API key. Please check your VITE_JSEARCH_API_KEY in .env file.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
    }

    const data = await response.json();
    
    if (data.status === 'OK' && data.data && data.data.length > 0) {
      const transformedJobs = data.data.map(transformJobData);
      setCache(query, location, transformedJobs);
      return transformedJobs;
    }

    if (data.status === 'OK' && (!data.data || data.data.length === 0)) {
      return [];
    }

    return [];
  } catch (error) {
    if (error.message && error.message.includes('API key')) {
      throw error;
    }
    console.warn('Falling back to mock data due to API error');
    return getMockJobs();
  }
};

/**
 * Get job details by ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object|null>} Job details or null
 */
export const getJobDetails = async (jobId) => {
  try {
    if (!JSEARCH_API_KEY) {
      console.warn('JSearch API key not configured. Using mock data.');
      return getMockJobById(jobId);
    }

    // JSearch doesn't have a direct "get by ID" endpoint, so we'll search and filter
    // In production, you might want to cache job details
    const jobs = await searchJobs({ query: '', numPages: 10 });
    const job = jobs.find(j => j.id === jobId || j.jobId === jobId);
    
    return job || getMockJobById(jobId);
  } catch (error) {
    console.error('Error fetching job details:', error);
    return getMockJobById(jobId);
  }
};

/**
 * Transform JSearch API job data to our format
 */
const transformJobData = (job) => {
  return {
    id: job.job_id || job.jobId || Date.now().toString(),
    jobId: job.job_id,
    title: job.job_title || job.title || 'Job Title',
    company: job.employer_name || job.company || 'Company',
    location: job.job_city && job.job_state 
      ? `${job.job_city}, ${job.job_state}` 
      : job.job_location || job.location || 'Location not specified',
    salary: job.job_min_salary && job.job_max_salary
      ? `$${job.job_min_salary.toLocaleString()} - $${job.job_max_salary.toLocaleString()}`
      : job.job_salary || job.salary || 'Salary not specified',
    type: job.job_employment_type || job.type || 'Full-time',
    posted: formatDatePosted(job.job_posted_at_datetime_utc || job.job_posted_at),
    description: job.job_description || job.description || '',
    requirements: extractRequirements(job.job_description || ''),
    responsibilities: extractResponsibilities(job.job_description || ''),
    benefits: extractBenefits(job.job_description || ''),
    link: job.job_apply_link || job.job_google_link || job.link || '#',
    jobIsRemote: job.job_is_remote || false,
    jobRequiredExperience: job.job_required_experience || null,
    jobRequiredSkills: job.job_required_skills || [],
    jobHighlights: job.job_highlights || {}
  };
};

/**
 * Format date posted to relative time
 */
const formatDatePosted = (dateString) => {
  if (!dateString) return 'Recently';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  } catch {
    return 'Recently';
  }
};

/**
 * Extract requirements from job description
 */
const extractRequirements = (description) => {
  if (!description) return [];
  
  const requirements = [];
  const lines = description.split('\n');
  let inRequirements = false;
  
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('requirement') || lowerLine.includes('qualification')) {
      inRequirements = true;
      continue;
    }
    if (inRequirements && (line.trim().startsWith('-') || line.trim().startsWith('•') || line.trim().match(/^\d+\./))) {
      requirements.push(line.trim().replace(/^[-•\d.\s]+/, ''));
      if (requirements.length >= 5) break;
    }
    if (inRequirements && (lowerLine.includes('responsibilit') || lowerLine.includes('benefit'))) {
      break;
    }
  }
  
  return requirements.length > 0 ? requirements : [
    'Relevant experience in the field',
    'Strong communication skills',
    'Ability to work in a team environment',
    'Problem-solving abilities',
    'Attention to detail'
  ];
};

/**
 * Extract responsibilities from job description
 */
const extractResponsibilities = (description) => {
  if (!description) return [];
  
  const responsibilities = [];
  const lines = description.split('\n');
  let inResponsibilities = false;
  
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('responsibilit') || lowerLine.includes('duties')) {
      inResponsibilities = true;
      continue;
    }
    if (inResponsibilities && (line.trim().startsWith('-') || line.trim().startsWith('•') || line.trim().match(/^\d+\./))) {
      responsibilities.push(line.trim().replace(/^[-•\d.\s]+/, ''));
      if (responsibilities.length >= 5) break;
    }
    if (inResponsibilities && (lowerLine.includes('requirement') || lowerLine.includes('benefit'))) {
      break;
    }
  }
  
  return responsibilities.length > 0 ? responsibilities : [
    'Perform job duties as assigned',
    'Collaborate with team members',
    'Meet project deadlines',
    'Maintain quality standards',
    'Contribute to team goals'
  ];
};

/**
 * Extract benefits from job description
 */
const extractBenefits = (description) => {
  if (!description) return [];
  
  const benefits = [];
  const lines = description.split('\n');
  let inBenefits = false;
  
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('benefit') || lowerLine.includes('perk') || lowerLine.includes('compensation')) {
      inBenefits = true;
      continue;
    }
    if (inBenefits && (line.trim().startsWith('-') || line.trim().startsWith('•') || line.trim().match(/^\d+\./))) {
      benefits.push(line.trim().replace(/^[-•\d.\s]+/, ''));
      if (benefits.length >= 5) break;
    }
  }
  
  return benefits.length > 0 ? benefits : [
    'Competitive salary',
    'Health insurance',
    '401(k) matching',
    'Flexible work arrangements',
    'Professional development opportunities'
  ];
};

/**
 * Mock jobs fallback (when API is not configured or fails)
 */
const getMockJobs = () => {
  return [
    {
      id: 1,
      title: 'Senior Software Engineer',
      company: 'Tech Innovations Inc.',
      location: 'San Francisco, CA',
      salary: '$120,000 - $150,000',
      type: 'Full-time',
      posted: '2 days ago',
      description: 'We are seeking a highly skilled Senior Software Engineer to join our dynamic team. You will be responsible for designing, developing, and maintaining scalable web applications using modern technologies.',
      requirements: [
        'Bachelor\'s degree in Computer Science or related field',
        '5+ years of experience in software development',
        'Proficiency in JavaScript, React, and Node.js',
        'Experience with cloud platforms (AWS, Azure, or GCP)',
        'Strong problem-solving and communication skills'
      ],
      responsibilities: [
        'Design and develop scalable web applications',
        'Collaborate with cross-functional teams',
        'Write clean, maintainable code',
        'Participate in code reviews',
        'Mentor junior developers'
      ],
      benefits: [
        'Competitive salary and equity package',
        'Comprehensive health insurance',
        '401(k) matching',
        'Flexible work arrangements',
        'Professional development opportunities'
      ],
      link: '#'
    },
    {
      id: 2,
      title: 'Product Manager',
      company: 'Digital Solutions LLC',
      location: 'New York, NY',
      salary: '$110,000 - $140,000',
      type: 'Full-time',
      posted: '5 days ago',
      description: 'Join our product team to drive innovation and deliver exceptional user experiences. You will work closely with engineering, design, and business stakeholders to define and execute product strategy.',
      requirements: [
        'Bachelor\'s degree in Business, Engineering, or related field',
        '3+ years of product management experience',
        'Strong analytical and problem-solving skills',
        'Excellent communication and leadership abilities'
      ],
      responsibilities: [
        'Define product roadmap and strategy',
        'Collaborate with cross-functional teams',
        'Gather and prioritize product requirements',
        'Monitor product performance and metrics'
      ],
      benefits: [
        'Competitive salary',
        'Health and dental insurance',
        'Stock options',
        'Remote work flexibility'
      ],
      link: '#'
    },
    {
      id: 3,
      title: 'UX Designer',
      company: 'Creative Studios',
      location: 'Los Angeles, CA',
      salary: '$90,000 - $120,000',
      type: 'Full-time',
      posted: '1 week ago',
      description: 'We are looking for a talented UX Designer to create intuitive and engaging user experiences. You will work on a variety of projects from web applications to mobile interfaces.',
      requirements: [
        'Portfolio demonstrating UX design skills',
        '3+ years of experience in UX/UI design',
        'Proficiency in Figma, Sketch, or Adobe XD',
        'Understanding of user research methodologies'
      ],
      responsibilities: [
        'Create user flows and wireframes',
        'Design high-fidelity mockups',
        'Conduct user research and testing',
        'Collaborate with developers'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Creative work environment',
        'Professional development budget'
      ],
      link: '#'
    },
    {
      id: 4,
      title: 'Data Scientist',
      company: 'Analytics Corp',
      location: 'Seattle, WA',
      salary: '$130,000 - $160,000',
      type: 'Full-time',
      posted: '3 days ago',
      description: 'Join our data science team to extract insights from large datasets and build predictive models. You will work on challenging problems using machine learning and statistical analysis.',
      requirements: [
        'Master\'s or PhD in Statistics, Computer Science, or related field',
        '2+ years of experience in data science',
        'Proficiency in Python, R, or SQL',
        'Experience with machine learning frameworks'
      ],
      responsibilities: [
        'Analyze large datasets',
        'Build predictive models',
        'Present findings to stakeholders',
        'Collaborate with engineering teams'
      ],
      benefits: [
        'Competitive salary',
        'Health and dental insurance',
        '401(k) with company match',
        'Learning and development opportunities'
      ],
      link: '#'
    },
    {
      id: 5,
      title: 'Marketing Manager',
      company: 'Growth Marketing Inc.',
      location: 'Chicago, IL',
      salary: '$95,000 - $125,000',
      type: 'Full-time',
      posted: '4 days ago',
      description: 'We are seeking an experienced Marketing Manager to lead our marketing initiatives and drive brand awareness. You will develop and execute marketing strategies across multiple channels.',
      requirements: [
        'Bachelor\'s degree in Marketing or related field',
        '5+ years of marketing experience',
        'Experience with digital marketing tools',
        'Strong analytical and creative skills'
      ],
      responsibilities: [
        'Develop marketing strategies',
        'Manage marketing campaigns',
        'Analyze campaign performance',
        'Coordinate with sales and product teams'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Flexible PTO',
        'Marketing budget'
      ],
      link: '#'
    },
    {
      id: 6,
      title: 'Business Analyst',
      company: 'Strategy Solutions',
      location: 'Remote',
      salary: '$85,000 - $110,000',
      type: 'Full-time',
      posted: '6 days ago',
      description: 'Join our team as a Business Analyst to help organizations make data-driven decisions. You will analyze business processes, identify opportunities, and recommend solutions.',
      requirements: [
        'Bachelor\'s degree in Business, Finance, or related field',
        '3+ years of business analysis experience',
        'Strong analytical and problem-solving skills',
        'Excellent communication skills'
      ],
      responsibilities: [
        'Analyze business processes',
        'Gather and document requirements',
        'Create reports and presentations',
        'Collaborate with stakeholders'
      ],
      benefits: [
        'Competitive salary',
        'Remote work',
        'Health insurance',
        'Professional development'
      ],
      link: '#'
    }
  ];
};

const getMockJobById = (id) => {
  const mockJobs = getMockJobs();
  return mockJobs.find(j => j.id === parseInt(id)) || null;
};

export default {
  searchJobs,
  getJobDetails
};
